import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import HomeButton from './HomeButton.jsx'
import PremiumGate from './PremiumGate.jsx'
import Lodging from './Lodging.jsx'

// Helper functions for realistic data based on common destinations
const getEmergencyNumber = (country) => {
  const emergencyNumbers = {
    'France': '112',
    'Germany': '112', 
    'Spain': '112',
    'Italy': '112',
    'UK': '999',
    'United Kingdom': '999',
    'USA': '911',
    'United States': '911',
    'Canada': '911',
    'Australia': '000',
    'Japan': '110',
    'China': '110',
    'India': '112',
    'Brazil': '190'
  }
  return emergencyNumbers[country] || '112'
}

const getCurrency = (country) => {
  const currencies = {
    'France': 'Euro (EUR)',
    'Germany': 'Euro (EUR)',
    'Spain': 'Euro (EUR)',
    'Italy': 'Euro (EUR)',
    'UK': 'Pound Sterling (GBP)',
    'United Kingdom': 'Pound Sterling (GBP)',
    'USA': 'US Dollar (USD)',
    'United States': 'US Dollar (USD)',
    'Canada': 'Canadian Dollar (CAD)',
    'Australia': 'Australian Dollar (AUD)',
    'Japan': 'Japanese Yen (JPY)',
    'China': 'Chinese Yuan (CNY)',
    'India': 'Indian Rupee (INR)',
    'Brazil': 'Brazilian Real (BRL)'
  }
  return currencies[country] || `Local currency of ${country}`
}

const getTimezone = (destination, country) => {
  const timezones = {
    'France': 'Central European Time (CET/CEST) - UTC+1/+2',
    'Germany': 'Central European Time (CET/CEST) - UTC+1/+2', 
    'Spain': 'Central European Time (CET/CEST) - UTC+1/+2',
    'Italy': 'Central European Time (CET/CEST) - UTC+1/+2',
    'UK': 'Greenwich Mean Time (GMT/BST) - UTC+0/+1',
    'United Kingdom': 'Greenwich Mean Time (GMT/BST) - UTC+0/+1',
    'USA': 'Multiple timezones - Check specific city',
    'United States': 'Multiple timezones - Check specific city',
    'Canada': 'Multiple timezones - Check specific city',
    'Australia': 'Multiple timezones - Check specific city',
    'Japan': 'Japan Standard Time (JST) - UTC+9',
    'China': 'China Standard Time (CST) - UTC+8',
    'India': 'India Standard Time (IST) - UTC+5:30',
    'Brazil': 'Multiple timezones - Check specific city'
  }
  
  const currentTime = new Date().toLocaleString('en-US', {
    timeZone: country === 'France' ? 'Europe/Paris' :
              country === 'Germany' ? 'Europe/Berlin' :
              country === 'Spain' ? 'Europe/Madrid' :
              country === 'Italy' ? 'Europe/Rome' :
              country === 'UK' ? 'Europe/London' :
              country === 'Japan' ? 'Asia/Tokyo' :
              'UTC'
  })
  
  return `${timezones[country] || `Timezone for ${country}`} | Current time: ${currentTime}`
}

const openMaps = (address) => {
  const encodedAddress = encodeURIComponent(address)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  if (isIOS) {
    window.open(`http://maps.apple.com/?q=${encodedAddress}`, '_blank')
  } else {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
  }
}

function UsefulInfo({ userData, onNavigate }) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('useful')
  const [cityInfo, setCityInfo] = useState(null)
  const [emergencyContacts, setEmergencyContacts] = useState([])
  const [embassyInfo, setEmbassyInfo] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [privateDocuments, setPrivateDocuments] = useState([])
  const [travelInfo, setTravelInfo] = useState({
    flights: [],
    cars: [],
    trains: [],
    buses: []
  })
  const [expandedPerson, setExpandedPerson] = useState(null)
  const [showingForm, setShowingForm] = useState(null) // 'flight', 'car', 'train', 'bus', null
  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (window.navigationOptions?.tab) {
      setActiveTab(window.navigationOptions.tab)
      delete window.navigationOptions
    }
  }, [])

  useEffect(() => {
    loadUsefulInfoData()
    if (userData.destination && userData.country) {
      searchCityInfo()
      searchEmergencyContacts()
      searchEmbassyInfo()
    }
  }, [userData.destination, userData.country])

  const loadUsefulInfoData = () => {
    const tripData = Storage.getTripData()
    setPrivateDocuments(tripData.privateDocuments || [])
    setTravelInfo(tripData.travelInfo || {
      flights: [],
      cars: [],
      trains: [],
      buses: []
    })
  }

  const saveUsefulInfoData = (field, data) => {
    const tripData = Storage.getTripData()
    tripData[field] = data
    Storage.saveTripData(tripData)
  }

  // Travel Info Functions
  const addFlight = () => {
    setFormData({})
    setShowingForm('flight')
  }

  const submitFlightForm = () => {
    const { airline, flightNumber, departure, arrival, gate, flightDocument, boardingPass, connections } = formData
    if (!airline || !flightNumber || !departure || !arrival) {
      alert('Please upload flight document or fill in all required fields')
      return
    }
    
    const newFlight = {
      id: Date.now(),
      airline,
      flightNumber,
      departure,
      arrival,
      gate: gate || 'TBD',
      status: 'Scheduled',
      checkInStatus: 'Pending',
      checkInReminder: calculateCheckInTime(departure),
      flightDocument: flightDocument || null,
      boardingPass: boardingPass || null,
      gateStatus: 'Pending',
      connections: connections || [],
      isMultiLeg: (connections && connections.length > 0),
      totalFlightTime: calculateTotalFlightTime(departure, arrival, connections)
    }
    
    const updatedTravelInfo = { ...travelInfo, flights: [...travelInfo.flights, newFlight] }
    setTravelInfo(updatedTravelInfo)
    saveUsefulInfoData('travelInfo', updatedTravelInfo)
    setShowingForm(null)
    setFormData({})
  }

  const calculateTotalFlightTime = (departure, arrival, connections) => {
    try {
      const depDate = new Date(departure)
      const arrDate = new Date(arrival)
      const totalMinutes = Math.floor((arrDate - depDate) / (1000 * 60))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return `${hours}h ${minutes}m total travel time`
    } catch {
      return 'Travel time calculation pending'
    }
  }

  const addConnection = () => {
    const connections = formData.connections || []
    const newConnection = {
      id: Date.now(),
      stopoverAirport: '',
      stopoverCity: '',
      arrivalTime: '',
      departureTime: '',
      layoverDuration: '',
      connectingFlight: '',
      gate: ''
    }
    setFormData({
      ...formData,
      connections: [...connections, newConnection]
    })
  }

  const updateConnection = (connectionId, field, value) => {
    const connections = formData.connections || []
    const updatedConnections = connections.map(conn => 
      conn.id === connectionId ? { ...conn, [field]: value } : conn
    )
    setFormData({ ...formData, connections: updatedConnections })
  }

  const removeConnection = (connectionId) => {
    const connections = formData.connections || []
    const updatedConnections = connections.filter(conn => conn.id !== connectionId)
    setFormData({ ...formData, connections: updatedConnections })
  }

  const calculateCheckInTime = (departure) => {
    // Calculate 24 hours before departure for check-in reminder
    try {
      const depDate = new Date(departure)
      const checkInDate = new Date(depDate.getTime() - (24 * 60 * 60 * 1000))
      return checkInDate.toLocaleString()
    } catch {
      return 'Check airline policy'
    }
  }

  const handleFlightDocumentUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        // Simulate OCR extraction - In real implementation, use OCR service
        // Mock detecting if it's a multi-leg flight
        const hasConnections = Math.random() > 0.7 // 30% chance of connections for demo
        
        const mockConnections = hasConnections ? [{
          id: Date.now(),
          stopoverAirport: 'JFK',
          stopoverCity: 'New York',
          arrivalTime: 'Dec 15, 2024 - 1:15 PM',
          departureTime: 'Dec 15, 2024 - 3:30 PM',
          layoverDuration: '2h 15m',
          connectingFlight: 'XX456',
          gate: 'B12'
        }] : []
        
        setFormData({
          ...formData, 
          flightDocument: e.target.result,
          // Mock OCR results - in real app, extract these from document
          airline: 'Extracted Airline',
          flightNumber: 'XX123',
          departure: 'Dec 15, 2024 - 10:30 AM',
          arrival: hasConnections ? 'Dec 15, 2024 - 6:45 PM' : 'Dec 15, 2024 - 2:45 PM',
          connections: mockConnections
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleBoardingPassUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setFormData({
          ...formData, 
          boardingPass: e.target.result
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const addCar = () => {
    setFormData({})
    setShowingForm('car')
  }

  const submitCarForm = () => {
    const { company, carModel, pickupDate, returnDate, location } = formData
    if (!company || !carModel || !pickupDate || !returnDate || !location) {
      alert('Please fill in all required fields')
      return
    }
    
    const newCar = {
      id: Date.now(),
      company,
      carModel,
      pickupDate,
      returnDate,
      location,
      status: 'Reserved'
    }
    
    const updatedTravelInfo = { ...travelInfo, cars: [...travelInfo.cars, newCar] }
    setTravelInfo(updatedTravelInfo)
    saveUsefulInfoData('travelInfo', updatedTravelInfo)
    setShowingForm(null)
    setFormData({})
  }

  const addTrain = () => {
    setFormData({})
    setShowingForm('train')
  }

  const submitTrainForm = () => {
    const { trainLine, trainNumber, departure, arrival, platform } = formData
    if (!trainLine || !trainNumber || !departure || !arrival) {
      alert('Please fill in all required fields')
      return
    }
    
    const newTrain = {
      id: Date.now(),
      trainLine,
      trainNumber,
      departure,
      arrival,
      platform: platform || 'TBD',
      status: 'Scheduled'
    }
    
    const updatedTravelInfo = { ...travelInfo, trains: [...travelInfo.trains, newTrain] }
    setTravelInfo(updatedTravelInfo)
    saveUsefulInfoData('travelInfo', updatedTravelInfo)
    setShowingForm(null)
    setFormData({})
  }

  const addBus = () => {
    setFormData({})
    setShowingForm('bus')
  }

  const submitBusForm = () => {
    const { busCompany, busNumber, departure, arrival, terminal } = formData
    if (!busCompany || !busNumber || !departure || !arrival || !terminal) {
      alert('Please fill in all required fields')
      return
    }
    
    const newBus = {
      id: Date.now(),
      busCompany,
      busNumber,
      departure,
      arrival,
      terminal,
      status: 'Scheduled'
    }
    
    const updatedTravelInfo = { ...travelInfo, buses: [...travelInfo.buses, newBus] }
    setTravelInfo(updatedTravelInfo)
    saveUsefulInfoData('travelInfo', updatedTravelInfo)
    setShowingForm(null)
    setFormData({})
  }

  // Web Search Functions
  const searchCityInfo = async () => {
    if (!userData.destination || !userData.country) return
    
    setIsLoading(true)
    try {
      // Simulated search - In real implementation, you'd use WebSearch
      // const searchQuery = `${userData.destination} ${userData.country} travel guide overview`
      // const results = await WebSearch({ query: searchQuery })
      
      // Essential travel info only - no fake descriptions
      setCityInfo({
        currency: getCurrency(userData.country),
        languages: userData.country === 'France' ? 'French, English spoken in tourist areas' : 
                  userData.country === 'Germany' ? 'German, English widely spoken' :
                  userData.country === 'Spain' ? 'Spanish, English in tourist areas' :
                  userData.country === 'Italy' ? 'Italian, English in tourist areas' :
                  `Official languages of ${userData.country}`,
        timeZone: getTimezone(userData.destination, userData.country)
      })
    } catch (error) {
      console.error('Error searching city info:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const searchEmergencyContacts = async () => {
    if (!userData.destination || !userData.country) return
    
    try {
      // Real emergency contacts based on destination country
      setEmergencyContacts([
        {
          type: 'Emergency Services',
          name: 'Emergency Hotline',
          phone: getEmergencyNumber(userData.country),
          description: 'Police, Fire, Medical Emergency'
        },
        {
          type: 'Police',
          name: `${userData.destination} Police`,
          phone: userData.country === 'France' ? '17' : 
                 userData.country === 'Germany' ? '110' :
                 userData.country === 'UK' ? '101' :
                 userData.country === 'Spain' ? '091' :
                 userData.country === 'Italy' ? '113' :
                 'Check local directory',
          mapSearch: `police station in ${userData.destination}, ${userData.country}`,
          description: 'Local police contact'
        },
        {
          type: 'Medical Emergency',
          name: 'Ambulance/Medical',
          phone: userData.country === 'France' ? '15 (SAMU)' :
                 userData.country === 'Germany' ? '112' :
                 userData.country === 'UK' ? '999' :
                 userData.country === 'Spain' ? '061' :
                 userData.country === 'Italy' ? '118' :
                 'Medical emergency number',
          description: 'Medical emergency services'
        },
        {
          type: 'Hospital',
          name: `Public Hospital`,
          phone: userData.country === 'France' ? 'Check local hospital directory' :
                 userData.country === 'Germany' ? 'Contact hospital directly' :
                 'Hospital main line',
          mapSearch: `public hospital in ${userData.destination}, ${userData.country}`,
          description: 'Primary public healthcare facility'
        },
        {
          type: 'Private Hospital',
          name: `Private Hospital`,
          phone: 'Contact for private healthcare',
          mapSearch: `private hospital in ${userData.destination}, ${userData.country}`,
          description: 'Private healthcare with multilingual staff'
        },
        {
          type: 'Tourist Information',
          name: 'Tourist Information Center',
          phone: userData.country === 'France' ? '3265 (Tourist Info)' :
                 userData.country === 'Germany' ? 'Local tourist office' :
                 userData.country === 'Spain' ? 'Tourist police 902 102 112' :
                 'Local tourist office',
          mapSearch: `tourist information center in ${userData.destination}, ${userData.country}`,
          description: 'Tourist assistance and information'
        }
      ])
    } catch (error) {
      console.error('Error searching emergency contacts:', error)
    }
  }

  const searchEmbassyInfo = async () => {
    if (!userData.nationality || !userData.country) return
    
    try {
      // Embassy info - just name and map search
      setEmbassyInfo({
        name: `${userData.nationality} Embassy/Consulate in ${userData.country}`,
        mapSearch: `${userData.nationality} government embassy in ${userData.destination}, ${userData.country}`,
        services: [
          'Passport services',
          'Emergency assistance', 
          'Consular services',
          'Legal assistance',
          'Travel document replacement'
        ]
      })
    } catch (error) {
      console.error('Error searching embassy info:', error)
    }
  }


  // Document Management Functions
  const addPersonToDocuments = () => {
    const personName = prompt(t('usefulInfo.enterPersonNamePrompt'))
    if (!personName || personName.trim() === '') return
    
    const newPerson = {
      id: Date.now(),
      name: personName.trim(),
      documents: []
    }
    
    const updatedDocuments = [...privateDocuments, newPerson]
    setPrivateDocuments(updatedDocuments)
    saveUsefulInfoData('privateDocuments', updatedDocuments)
  }

  const addDocumentToPerson = (personId, documentType) => {
    // Create file input for document upload
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = documentType.needsBothSides // Allow multiple files for front/back
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files)
      if (files.length === 0) return
      
      const processFile = (file, side = null) => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = (event) => {
            resolve({
              id: Date.now() + Math.random(),
              type: documentType.type,
              side: side,
              data: event.target.result,
              fileName: file.name,
              uploadDate: new Date().toISOString()
            })
          }
          reader.readAsDataURL(file)
        })
      }
      
      Promise.all(files.map((file, index) => {
        if (documentType.needsBothSides) {
          return processFile(file, index === 0 ? 'front' : 'back')
        } else {
          return processFile(file)
        }
      })).then(documents => {
        const updatedDocuments = privateDocuments.map(person => {
          if (person.id === personId) {
            return {
              ...person,
              documents: [...person.documents, ...documents]
            }
          }
          return person
        })
        
        setPrivateDocuments(updatedDocuments)
        saveUsefulInfoData('privateDocuments', updatedDocuments)
      })
    }
    
    input.click()
  }

  const documentTypes = [
    { type: t('usefulInfo.documentTypes.idCard'), needsBothSides: true, icon: '🪪' },
    { type: t('usefulInfo.documentTypes.passport'), needsBothSides: false, icon: '📔' },
    { type: t('usefulInfo.documentTypes.driverLicense'), needsBothSides: true, icon: '🚗' },
    { type: t('usefulInfo.documentTypes.healthCard'), needsBothSides: true, icon: '🏥' },
    { type: t('usefulInfo.documentTypes.insuranceCard'), needsBothSides: true, icon: '🛡️' },
    { type: t('usefulInfo.documentTypes.creditCard'), needsBothSides: true, icon: '💳' },
    { type: t('usefulInfo.documentTypes.otherDocument'), needsBothSides: false, icon: '📄' }
  ]

  const tabs = [
    { id: 'useful', name: t('usefulInfo.tabs.usefulInfo'), icon: 'ℹ️' },
    { id: 'private', name: t('usefulInfo.tabs.privateInfo'), icon: '🔒' },
    { id: 'travel', name: t('usefulInfo.tabs.travelInfo'), icon: '✈️' },
    { id: 'lodging', name: t('usefulInfo.tabs.lodging'), icon: '🏨' }
  ]

  const renderUsefulInfoTab = () => (
    <div className="useful-info-tab">
      {/* City Description */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        margin: '0 0 20px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#B8A082',
          margin: '0 0 12px 0'
        }}>
          {t('usefulInfo.about', { city: userData.destination })}
        </h3>
        {isLoading ? (
          <p style={{ color: '#999' }}>{t('usefulInfo.loadingCity')}</p>
        ) : cityInfo ? (
          <div>
            {/* Prominent Timezone Display */}
            <div style={{
              background: '#f8f6f0',
              border: '2px solid #D4A574',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '4px'
              }}>
                {t('usefulInfo.localTime')}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '700',
                color: '#B8A082'
              }}>
                {cityInfo.timeZone}
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '12px',
              fontSize: '12px',
              color: '#8B7355'
            }}>
              <div><strong>{t('usefulInfo.currencyLabel')}</strong> {cityInfo.currency}</div>
              <div><strong>{t('usefulInfo.languagesLabel')}</strong> {cityInfo.languages}</div>
            </div>
          </div>
        ) : (
          <p style={{ color: '#999' }}>{t('usefulInfo.cityAutoLoad')}</p>
        )}
      </div>

      {/* Emergency Contacts */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        margin: '0 0 20px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#B8A082',
          margin: '0 0 16px 0'
        }}>
          {t('usefulInfo.emergency')}
        </h3>
        {emergencyContacts.map((contact, index) => (
          <div key={index} style={{
            padding: '12px 0',
            borderBottom: index < emergencyContacts.length - 1 ? '1px solid #f0f0f0' : 'none'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '4px'
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '2px'
                }}>
                  {contact.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#B8A082',
                  fontWeight: '600'
                }}>
                  {contact.type}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#8B7355'
                }}>
                  {contact.phone}
                </div>
                {(contact.address || contact.mapSearch) && (
                  <div>
                    {contact.address && (
                      <div style={{
                        fontSize: '11px',
                        color: '#666',
                        marginBottom: '4px'
                      }}>
                        {contact.address}
                      </div>
                    )}
                    <button
                      onClick={() => openMaps(contact.mapSearch || contact.address)}
                      style={{
                        background: '#C4714A',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {t('usefulInfo.findOnMaps')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <p style={{
              fontSize: '12px',
              color: '#666',
              margin: '0'
            }}>
              {contact.description}
            </p>
          </div>
        ))}
      </div>

      {/* Embassy Information */}
      {embassyInfo && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          margin: '0 0 20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#B8A082',
            margin: '0 0 16px 0'
          }}>
            🏛️ {embassyInfo.name}
          </h3>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <button
                onClick={() => openMaps(embassyInfo.mapSearch)}
                style={{
                  background: '#C4714A',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginBottom: '8px'
                }}
              >
                📍 Find Embassy on Maps
              </button>
            </div>
          </div>
          <div>
            <strong style={{ fontSize: '14px', color: '#8B7355' }}>Services:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              {embassyInfo.services.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )

  const renderPrivateInfoTab = () => (
    <div className="private-info-tab">
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        margin: '0 0 20px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#B8A082',
            margin: '0'
          }}>
            {t('usefulInfo.privateDocuments')}
          </h3>
          <button
            onClick={addPersonToDocuments}
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('usefulInfo.addPerson')}
          </button>
        </div>

        {privateDocuments.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <p>{t('usefulInfo.noDocuments')}</p>
            <p style={{ fontSize: '12px' }}>{t('usefulInfo.addPeopleDocuments')}</p>
          </div>
        ) : (
          privateDocuments.map(person => (
            <div key={person.id} style={{
              border: '2px solid #ede4d3',
              borderRadius: '12px',
              margin: '0 0 16px 0',
              overflow: 'hidden'
            }}>
              {/* Person Header */}
              <button
                onClick={() => setExpandedPerson(expandedPerson === person.id ? null : person.id)}
                style={{
                  width: '100%',
                  background: expandedPerson === person.id ? '#f8f6f0' : 'white',
                  border: 'none',
                  padding: '16px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#8B7355',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>👤 {person.name}</span>
                <span style={{
                  fontSize: '12px',
                  color: '#B8A082'
                }}>
                  {person.documents.length} docs {expandedPerson === person.id ? '▼' : '▶'}
                </span>
              </button>

              {/* Expanded Content */}
              {expandedPerson === person.id && (
                <div style={{ padding: '0 20px 20px 20px' }}>
                  
                  {/* Document Types Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    {documentTypes.map(docType => (
                      <button
                        key={docType.type}
                        onClick={() => addDocumentToPerson(person.id, docType)}
                        style={{
                          background: '#f8f6f0',
                          border: '2px dashed #B8A082',
                          borderRadius: '12px',
                          padding: '16px 12px',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>{docType.icon}</div>
                        <div style={{
                          fontSize: '10px',
                          color: '#8B7355',
                          fontWeight: '600'
                        }}>
                          {docType.type}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Uploaded Documents */}
                  {person.documents.length > 0 && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        color: '#8B7355',
                        margin: '0 0 12px 0'
                      }}>
                        Uploaded Documents:
                      </h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '12px'
                      }}>
                        {person.documents.map(doc => (
                          <div key={doc.id} style={{
                            border: '1px solid #ede4d3',
                            borderRadius: '8px',
                            padding: '8px',
                            textAlign: 'center'
                          }}>
                            <img
                              src={doc.data}
                              alt={doc.type}
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                marginBottom: '4px'
                              }}
                            />
                            <div style={{
                              fontSize: '11px',
                              color: '#8B7355',
                              fontWeight: '600'
                            }}>
                              {doc.type}
                              {doc.side && ` (${doc.side})`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )

  const renderTravelInfoTab = () => (
    <div className="travel-info-tab">
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        margin: '0 0 20px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#B8A082',
          margin: '0 0 16px 0'
        }}>
          {t('usefulInfo.travelInformation')}
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {/* Flight Card */}
          <div style={{
            border: '2px solid #ede4d3',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>✈️</div>
            <h4 style={{
              fontSize: '14px',
              color: '#8B7355',
              margin: '0 0 8px 0'
            }}>
              {t('usefulInfo.flightInfo')}
            </h4>
            <button
              onClick={addFlight}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('usefulInfo.addFlight')}
            </button>
          </div>

          {/* Car Card */}
          <div style={{
            border: '2px solid #ede4d3',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚗</div>
            <h4 style={{
              fontSize: '14px',
              color: '#8B7355',
              margin: '0 0 8px 0'
            }}>
              {t('usefulInfo.carRental')}
            </h4>
            <button
              onClick={addCar}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('usefulInfo.addCar')}
            </button>
          </div>

          {/* Train Card */}
          <div style={{
            border: '2px solid #ede4d3',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚆</div>
            <h4 style={{
              fontSize: '14px',
              color: '#8B7355',
              margin: '0 0 8px 0'
            }}>
              {t('usefulInfo.trainTickets')}
            </h4>
            <button
              onClick={addTrain}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('usefulInfo.addTrain')}
            </button>
          </div>

          {/* Bus Card */}
          <div style={{
            border: '2px solid #ede4d3',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚌</div>
            <h4 style={{
              fontSize: '14px',
              color: '#8B7355',
              margin: '0 0 8px 0'
            }}>
              {t('usefulInfo.busTickets')}
            </h4>
            <button
              onClick={addBus}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('usefulInfo.addBus')}
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showingForm && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            background: 'rgba(0,0,0,0.5)',
            zIndex: '9999',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              {showingForm === 'flight' && (
                <div>
                  <h3 style={{ fontSize: '18px', color: '#8B7355', marginBottom: '16px' }}>{t('usefulInfo.addFlight')}</h3>
                  
                  {/* Document Upload Section */}
                  <div style={{
                    background: '#f8f6f0',
                    border: '2px dashed #B8A082',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
                    <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '8px' }}>{t('usefulInfo.uploadFlightDocument')}</h4>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                      {t('usefulInfo.uploadFlightDocumentPrompt')}
                      <br />{t('usefulInfo.extractDetailsAuto')}
                    </p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFlightDocumentUpload}
                      style={{ display: 'none' }}
                      id="flight-document-upload"
                    />
                    <label
                      htmlFor="flight-document-upload"
                      style={{
                        display: 'inline-block',
                        background: '#C4714A',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {t('usefulInfo.uploadDocumentButton')}
                    </label>
                    {formData.flightDocument && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#8B7355' }}>
                        ✅ Document uploaded - details extracted
                      </div>
                    )}
                  </div>

                  {/* Manual Entry Section */}
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '12px' }}>
                      {t('usefulInfo.flightDetails')} {formData.flightDocument && '(Auto-filled from document)'}
                    </h4>
                    
                    <input
                      type="text"
                      placeholder={t('usefulInfo.placeholders.airline')}
                      value={formData.airline || ''}
                      onChange={(e) => setFormData({...formData, airline: e.target.value})}
                      style={{
                        width: '100%', padding: '12px', marginBottom: '12px',
                        border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px',
                        background: formData.flightDocument ? '#f0f8ff' : 'white'
                      }}
                    />

                    <input
                      type="text"
                      placeholder={t('usefulInfo.placeholders.flightNumber')}
                      value={formData.flightNumber || ''}
                      onChange={(e) => setFormData({...formData, flightNumber: e.target.value})}
                      style={{
                        width: '100%', padding: '12px', marginBottom: '12px',
                        border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px',
                        background: formData.flightDocument ? '#f0f8ff' : 'white'
                      }}
                    />

                    <input
                      type="text"
                      placeholder={t('usefulInfo.placeholders.departure')}
                      value={formData.departure || ''}
                      onChange={(e) => setFormData({...formData, departure: e.target.value})}
                      style={{
                        width: '100%', padding: '12px', marginBottom: '12px',
                        border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px',
                        background: formData.flightDocument ? '#f0f8ff' : 'white'
                      }}
                    />

                    <input
                      type="text"
                      placeholder={t('usefulInfo.placeholders.arrival')}
                      value={formData.arrival || ''}
                      onChange={(e) => setFormData({...formData, arrival: e.target.value})}
                      style={{
                        width: '100%', padding: '12px', marginBottom: '12px',
                        border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px',
                        background: formData.flightDocument ? '#f0f8ff' : 'white'
                      }}
                    />

                    <input
                      type="text"
                      placeholder={t('usefulInfo.placeholders.gate')}
                      value={formData.gate || ''}
                      onChange={(e) => setFormData({...formData, gate: e.target.value})}
                      style={{
                        width: '100%', padding: '12px', marginBottom: '12px',
                        border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Connections Section */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontSize: '14px', color: '#8B7355', margin: '0' }}>
                        {t('usefulInfo.connections')}
                      </h4>
                      <button
                        type="button"
                        onClick={addConnection}
                        style={{
                          background: '#4A90E2',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {t('usefulInfo.addStopover')}
                      </button>
                    </div>

                    {formData.connections && formData.connections.length > 0 ? (
                      formData.connections.map((connection, index) => (
                        <div key={connection.id} style={{
                          background: '#fff8e1',
                          border: '2px solid #ffcc02',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '12px', color: '#e65100' }}>
                              Connection #{index + 1}
                            </strong>
                            <button
                              type="button"
                              onClick={() => removeConnection(connection.id)}
                              style={{
                                background: '#ff6b6b',
                                color: 'white',
                                border: 'none',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                            <input
                              type="text"
                              placeholder="Airport Code (e.g., JFK)"
                              value={connection.stopoverAirport}
                              onChange={(e) => updateConnection(connection.id, 'stopoverAirport', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                            <input
                              type="text"
                              placeholder="City (e.g., New York)"
                              value={connection.stopoverCity}
                              onChange={(e) => updateConnection(connection.id, 'stopoverCity', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                            <input
                              type="text"
                              placeholder="Arrival Time"
                              value={connection.arrivalTime}
                              onChange={(e) => updateConnection(connection.id, 'arrivalTime', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Departure Time"
                              value={connection.departureTime}
                              onChange={(e) => updateConnection(connection.id, 'departureTime', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <input
                              type="text"
                              placeholder="Layover Duration"
                              value={connection.layoverDuration}
                              onChange={(e) => updateConnection(connection.id, 'layoverDuration', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Next Flight #"
                              value={connection.connectingFlight}
                              onChange={(e) => updateConnection(connection.id, 'connectingFlight', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Gate"
                              value={connection.gate}
                              onChange={(e) => updateConnection(connection.id, 'gate', e.target.value)}
                              style={{
                                padding: '8px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '12px'
                              }}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '16px',
                        color: '#999',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }}>
                        {t('usefulInfo.directFlight')}
                      </div>
                    )}
                  </div>

                  {/* Boarding Pass Section */}
                  <div style={{
                    background: '#f0f8ff',
                    border: '2px dashed #4A90E2',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎫</div>
                    <h4 style={{ fontSize: '14px', color: '#4A90E2', marginBottom: '8px' }}>{t('usefulInfo.boardingPass')}</h4>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                      {t('usefulInfo.uploadBoardingPassPrompt')}
                    </p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleBoardingPassUpload}
                      style={{ display: 'none' }}
                      id="boarding-pass-upload"
                    />
                    <label
                      htmlFor="boarding-pass-upload"
                      style={{
                        display: 'inline-block',
                        background: '#4A90E2',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {t('usefulInfo.uploadBoardingPassButton')}
                    </label>
                    {formData.boardingPass && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#4A90E2' }}>
                        ✅ Boarding pass saved
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showingForm === 'car' && (
                <div>
                  <h3 style={{ fontSize: '18px', color: '#8B7355', marginBottom: '16px' }}>{t('usefulInfo.addCar')}</h3>
                  
                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.company')}
                    value={formData.company || ''}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.carModel')}
                    value={formData.carModel || ''}
                    onChange={(e) => setFormData({...formData, carModel: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.pickupDate')}
                    value={formData.pickupDate || ''}
                    onChange={(e) => setFormData({...formData, pickupDate: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.returnDate')}
                    value={formData.returnDate || ''}
                    onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.pickupLocation')}
                    value={formData.location || ''}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '16px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {showingForm === 'train' && (
                <div>
                  <h3 style={{ fontSize: '18px', color: '#8B7355', marginBottom: '16px' }}>{t('usefulInfo.addTrain')}</h3>
                  
                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.trainCompany')}
                    value={formData.trainLine || ''}
                    onChange={(e) => setFormData({...formData, trainLine: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.trainNumber')}
                    value={formData.trainNumber || ''}
                    onChange={(e) => setFormData({...formData, trainNumber: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.departure')}
                    value={formData.departure || ''}
                    onChange={(e) => setFormData({...formData, departure: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.arrival')}
                    value={formData.arrival || ''}
                    onChange={(e) => setFormData({...formData, arrival: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.platform')}
                    value={formData.platform || ''}
                    onChange={(e) => setFormData({...formData, platform: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '16px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {showingForm === 'bus' && (
                <div>
                  <h3 style={{ fontSize: '18px', color: '#8B7355', marginBottom: '16px' }}>{t('usefulInfo.addBus')}</h3>
                  
                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.busCompany')}
                    value={formData.busCompany || ''}
                    onChange={(e) => setFormData({...formData, busCompany: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.busNumber')}
                    value={formData.busCompany || ''}
                    onChange={(e) => setFormData({...formData, busNumber: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.departure')}
                    value={formData.departure || ''}
                    onChange={(e) => setFormData({...formData, departure: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.arrival')}
                    value={formData.arrival || ''}
                    onChange={(e) => setFormData({...formData, arrival: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '12px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />

                  <input
                    type="text"
                    placeholder={t('usefulInfo.placeholders.terminal')}
                    value={formData.terminal || ''}
                    onChange={(e) => setFormData({...formData, terminal: e.target.value})}
                    style={{
                      width: '100%', padding: '12px', marginBottom: '16px',
                      border: '2px solid #ede4d3', borderRadius: '8px', fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {setShowingForm(null); setFormData({})}}
                  style={{
                    background: '#f0f0f0',
                    color: '#666',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('usefulInfo.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (showingForm === 'flight') submitFlightForm()
                    else if (showingForm === 'car') submitCarForm()
                    else if (showingForm === 'train') submitTrainForm()
                    else if (showingForm === 'bus') submitBusForm()
                  }}
                  style={{
                    background: '#C4714A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {showingForm === 'flight' ? t('usefulInfo.addFlightButton') : showingForm === 'car' ? t('usefulInfo.addCarButton') : showingForm === 'train' ? t('usefulInfo.addTrainButton') : t('usefulInfo.addBusButton')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Travel Information Display */}
        <div style={{ marginTop: '20px' }}>
          {/* Flights */}
          {travelInfo.flights.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: '2px solid #ede4d3'
            }}>
              <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '12px' }}>✈️ Flights</h4>
              {travelInfo.flights.map(flight => (
                <div key={flight.id} style={{
                  background: '#f8f6f0',
                  border: '2px solid #ede4d3',
                  padding: '16px',
                  borderRadius: '12px',
                  marginBottom: '12px',
                  fontSize: '13px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      {flight.airline} {flight.flightNumber}
                    </div>
                    {flight.isMultiLeg && (
                      <div style={{
                        background: '#ff9800',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {flight.connections.length} STOP{flight.connections.length > 1 ? 'S' : ''}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '4px' }}>🛫 {flight.departure}</div>
                  <div style={{ marginBottom: '4px' }}>🛬 {flight.arrival}</div>
                  <div style={{ marginBottom: '4px' }}>🚪 Gate: {flight.gate}</div>
                  {flight.totalFlightTime && (
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                      ⏱️ {flight.totalFlightTime}
                    </div>
                  )}

                  {/* Connection Details */}
                  {flight.connections && flight.connections.length > 0 && (
                    <div style={{
                      background: '#fff3e0',
                      border: '1px solid #ffcc02',
                      borderRadius: '8px',
                      padding: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '4px', color: '#e65100' }}>
                        🔄 Connections:
                      </div>
                      {flight.connections.map((connection, index) => (
                        <div key={connection.id} style={{ fontSize: '11px', marginBottom: '4px' }}>
                          <strong>Stop #{index + 1}:</strong> {connection.stopoverAirport} ({connection.stopoverCity})
                          <br />
                          <span style={{ color: '#666' }}>
                            Layover: {connection.layoverDuration} | Next Flight: {connection.connectingFlight}
                            {connection.gate && ` | Gate: ${connection.gate}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Check-in Reminder */}
                  {flight.checkInStatus === 'Pending' && (
                    <div style={{
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '8px',
                      padding: '8px',
                      marginBottom: '8px',
                      fontSize: '12px'
                    }}>
                      ⏰ <strong>Check-in Reminder:</strong> {flight.checkInReminder}
                      <br />
                      <small>Complete online check-in 24hrs before departure</small>
                    </div>
                  )}

                  {/* Document Status */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    {flight.flightDocument && (
                      <div style={{
                        background: '#d1ecf1',
                        color: '#0c5460',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px'
                      }}>
                        📄 E-ticket saved
                      </div>
                    )}
                    {flight.boardingPass && (
                      <div style={{
                        background: '#d4edda',
                        color: '#155724',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px'
                      }}>
                        🎫 Boarding pass ready
                      </div>
                    )}
                  </div>

                  <div style={{ color: '#B8A082', fontSize: '12px' }}>
                    Status: {flight.status} | Check-in: {flight.checkInStatus}
                    {flight.gateStatus === 'Updated' && ' | Gate confirmed via API'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cars */}
          {travelInfo.cars.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: '2px solid #ede4d3'
            }}>
              <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '12px' }}>🚗 Car Rentals</h4>
              {travelInfo.cars.map(car => (
                <div key={car.id} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  fontSize: '12px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {car.company} - {car.carModel}
                  </div>
                  <div>📍 {car.location}</div>
                  <div>📅 {car.pickupDate} → {car.returnDate}</div>
                  <div style={{ color: '#B8A082' }}>Status: {car.status}</div>
                </div>
              ))}
            </div>
          )}

          {/* Trains */}
          {travelInfo.trains.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: '2px solid #ede4d3'
            }}>
              <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '12px' }}>🚆 Trains</h4>
              {travelInfo.trains.map(train => (
                <div key={train.id} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  fontSize: '12px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {train.trainLine} {train.trainNumber}
                  </div>
                  <div>🚂 {train.departure}</div>
                  <div>🏁 {train.arrival}</div>
                  <div>🚉 Platform: {train.platform}</div>
                  <div style={{ color: '#B8A082' }}>Status: {train.status}</div>
                </div>
              ))}
            </div>
          )}

          {/* Buses */}
          {travelInfo.buses.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              border: '2px solid #ede4d3'
            }}>
              <h4 style={{ fontSize: '14px', color: '#8B7355', marginBottom: '12px' }}>🚌 Buses</h4>
              {travelInfo.buses.map(bus => (
                <div key={bus.id} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  fontSize: '12px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {bus.busCompany} {bus.busNumber}
                  </div>
                  <div>🚌 {bus.departure}</div>
                  <div>🏁 {bus.arrival}</div>
                  <div>🚏 {bus.terminal}</div>
                  <div style={{ color: '#B8A082' }}>Status: {bus.status}</div>
                </div>
              ))}
            </div>
          )}

          {(travelInfo.flights.length === 0 && travelInfo.cars.length === 0 && 
            travelInfo.trains.length === 0 && travelInfo.buses.length === 0) && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              {t('usefulInfo.noTravelInfo')}<br />
              {t('usefulInfo.useTravelButtons')}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderCityGuideTab = () => (
    <div className="city-guide-tab">
      {cityGuide ? (
        <div>
          {/* Top Attractions */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            margin: '0 0 16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#B8A082',
              margin: '0 0 12px 0'
            }}>
              🏛️ Top Attractions
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px'
            }}>
              {cityGuide.topAttractions.map((attraction, index) => (
                <div key={index} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#8B7355'
                }}>
                  {attraction}
                </div>
              ))}
            </div>
          </div>

          {/* Restaurants */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            margin: '0 0 16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#B8A082',
              margin: '0 0 12px 0'
            }}>
              🍽️ Restaurants & Food
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px'
            }}>
              {cityGuide.restaurants.map((restaurant, index) => (
                <div key={index} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#8B7355'
                }}>
                  {restaurant}
                </div>
              ))}
            </div>
          </div>

          {/* Activities */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            margin: '0 0 16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#B8A082',
              margin: '0 0 12px 0'
            }}>
              🎯 Activities & Tours
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px'
            }}>
              {cityGuide.activities.map((activity, index) => (
                <div key={index} style={{
                  background: '#f8f6f0',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#8B7355'
                }}>
                  {activity}
                </div>
              ))}
            </div>
          </div>

          {/* Local Tips */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            margin: '0 0 16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#B8A082',
              margin: '0 0 12px 0'
            }}>
              💡 Local Tips
            </h3>
            <ul style={{
              margin: '0',
              paddingLeft: '20px',
              fontSize: '14px',
              color: '#666'
            }}>
              {cityGuide.localTips.map((tip, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <p style={{ color: '#999' }}>Loading city guide...</p>
        </div>
      )}
    </div>
  )

  return (
    <div className="container useful-info" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>
      <HomeButton onNavigate={onNavigate} />
      
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingTop: '60px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          fontFamily: "'Noto Serif', serif",
          textTransform: 'uppercase'
        }}>{t('usefulInfo.title')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0 0 16px 0',
          opacity: '0.7'
        }}>{t('usefulInfo.everything', { city: userData.destination?.toUpperCase() || t('mainPage.yourCity').toUpperCase() })}</p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '8px',
        margin: '0 0 20px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '4px',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? '#5B8FA8' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#8B7355',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              transition: 'all 0.3s ease'
            }}
          >
            <div>{tab.icon}</div>
            <div style={{ marginTop: '4px' }}>{tab.name}</div>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'useful' && renderUsefulInfoTab()}
      {activeTab === 'private' && (
        <PremiumGate
          feature="document_storage"
          onNavigate={onNavigate}
        >
          {renderPrivateInfoTab()}
        </PremiumGate>
      )}
      {activeTab === 'travel' && (
        <PremiumGate
          feature="travel_info"
          onNavigate={onNavigate}
        >
          {renderTravelInfoTab()}
        </PremiumGate>
      )}
      {activeTab === 'lodging' && (
        <Lodging userData={userData} onNavigate={onNavigate} embedded={true} />
      )}

      {/* Back Button */}
      <div style={{ textAlign: 'center', margin: '30px 0 20px 0' }}>
        <button 
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          {t('navigation.backToHome')}
        </button>
      </div>
    </div>
  )
}

export default UsefulInfo